import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { loginUser } from '../services/api';
import PricingSection from '../components/landing/PricingSection';

const UI = {
  bg: '#ffffff',
  text: '#0b0b0f',
  muted: '#5b5b66',
  border: '#e7e7ee',
  card: '#ffffff',
  card2: '#fafafe',
  accent: '#0b0b0f',
  accentHover: '#1a1a22',
  danger: '#b42318',
  dangerBg: '#fee4e2',
};

async function healthCheck() {
  try {
    const base = process.env.REACT_APP_API_BASE_URL || '';
    const res = await fetch(`${base}/api/vukz/health`, { method: 'GET' });
    if (!res.ok) return { ok: false };
    const data = await res.json().catch(() => ({}));
    return { ok: true, data };
  } catch {
    return { ok: false };
  }
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [health, setHealth] = useState({ checked: false, ok: false });

  useEffect(() => {
    let mounted = true;
    (async () => {
      const h = await healthCheck();
      if (mounted) setHealth({ checked: true, ok: !!h.ok, data: h.data });
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const canSubmit = useMemo(() => {
    return !!formData.email && !!formData.password && !loading;
  }, [formData.email, formData.password, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await loginUser(formData);

      if (response?.success) {
        await login(formData.email, formData.password);
        navigate('/feed');
      } else {
        setError(response?.error || 'Não foi possível fazer login.');
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao conectar ao servidor.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: UI.bg,
        color: UI.text,
      }}
    >
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b" style={{ borderColor: UI.border }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-xl font-semibold tracking-tight">
              VUKZ <span className="opacity-70">AI</span>
            </div>

            {/* Status real (health) */}
            <div
              className="hidden md:flex items-center gap-2 px-3 py-1 rounded-full border text-xs"
              style={{
                borderColor: UI.border,
                color: UI.muted,
                background: '#ffffff',
              }}
              title="Status do sistema (endpoint /api/vukz/health)"
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{
                  backgroundColor: !health.checked ? '#999' : health.ok ? '#12b76a' : '#f04438',
                }}
              />
              <span className="font-mono">
                {!health.checked ? 'checando...' : health.ok ? 'online' : 'offline'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <a
              href="#planos"
              className="text-sm font-medium hover:opacity-80 transition-opacity"
              style={{ color: UI.muted }}
            >
              Planos
            </a>
            <Link
              to="/register"
              className="text-sm font-medium hover:opacity-80 transition-opacity"
              style={{ color: UI.text }}
            >
              Criar conta
            </Link>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
          {/* Left - Login */}
          <section className="flex">
            <div
              className="w-full rounded-2xl border p-8 md:p-10"
              style={{ borderColor: UI.border, background: UI.card }}
            >
              <div className="mb-8">
                <h1 className="text-3xl md:text-4xl font-semibold leading-tight">
                  Performance intelligence,
                  <span className="block" style={{ color: UI.muted }}>
                    sem ruído.
                  </span>
                </h1>
                <p className="mt-3 text-base" style={{ color: UI.muted }}>
                  Entre para acessar rankings, painéis e ferramentas do treinador/atleta — com dados reais do seu ecossistema.
                </p>
              </div>

              {error && (
                <div
                  className="mb-6 p-3 rounded-lg border text-sm"
                  style={{
                    borderColor: UI.danger,
                    background: UI.dangerBg,
                    color: UI.danger,
                  }}
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: UI.text }}>
                    E-mail
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    required
                    autoComplete="email"
                    className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2"
                    style={{
                      borderColor: UI.border,
                      background: '#fff',
                    }}
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: UI.text }}>
                    Senha
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                    required
                    autoComplete="current-password"
                    className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2"
                    style={{
                      borderColor: UI.border,
                      background: '#fff',
                    }}
                    placeholder="••••••••"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full py-3 rounded-lg font-semibold transition-all"
                  style={{
                    background: canSubmit ? UI.accent : '#bdbdc7',
                    color: '#fff',
                  }}
                  onMouseEnter={(e) => {
                    if (canSubmit) e.currentTarget.style.background = UI.accentHover;
                  }}
                  onMouseLeave={(e) => {
                    if (canSubmit) e.currentTarget.style.background = UI.accent;
                  }}
                >
                  {loading ? 'Entrando…' : 'Entrar'}
                </button>

                <div className="flex items-center justify-between pt-2 text-sm" style={{ color: UI.muted }}>
                  <span>Sem acesso?</span>
                  <Link to="/register" className="font-medium hover:opacity-80 transition-opacity" style={{ color: UI.text }}>
                    Criar nova conta
                  </Link>
                </div>
              </form>

              <p className="mt-8 text-xs" style={{ color: UI.muted }}>
                🔒 Segurança: sessão autenticada e dados protegidos. Nada é publicado em seu nome.
              </p>
            </div>
          </section>

          {/* Right - Value props (sem números fake) */}
          <section className="flex">
            <div
              className="w-full rounded-2xl border p-8 md:p-10 relative overflow-hidden"
              style={{ borderColor: UI.border, background: UI.card2 }}
            >
              {/* detalhe visual minimal */}
              <div
                aria-hidden="true"
                className="absolute -top-24 -right-24 w-72 h-72 rounded-full"
                style={{
                  background: 'radial-gradient(circle at 30% 30%, rgba(11,11,15,0.12), transparent 60%)',
                }}
              />
              <div
                aria-hidden="true"
                className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full"
                style={{
                  background: 'radial-gradient(circle at 70% 70%, rgba(11,11,15,0.10), transparent 60%)',
                }}
              />

              <div className="relative">
                <h2 className="text-2xl font-semibold">O que você acessa aqui</h2>
                <p className="mt-3 text-base" style={{ color: UI.muted }}>
                  Um painel feito para operação esportiva: treinador, atleta e gestão técnica — com integração progressiva com visão computacional e sensores.
                </p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    {
                      title: 'Rankings e provas',
                      desc: 'Filtros por ano, prova e recortes (VUKZ / Brasil / simulações).',
                    },
                    {
                      title: 'Meus atletas',
                      desc: 'Acompanhe posição, evolução e comparação por contexto competitivo.',
                    },
                    {
                      title: 'Treino e anotações',
                      desc: 'Plano acessível, com registro e histórico (sem planilhas soltas).',
                    },
                    {
                      title: 'Governança e acesso',
                      desc: 'Perfis por função (atleta, treinador, staff) com RBAC.',
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="rounded-xl border p-5"
                      style={{ borderColor: UI.border, background: '#fff' }}
                    >
                      <div className="font-semibold">{item.title}</div>
                      <div className="mt-2 text-sm" style={{ color: UI.muted }}>
                        {item.desc}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap gap-2">
                  {['Natação', 'Futebol', 'RBAC', 'Azure-ready'].map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs border"
                      style={{ borderColor: UI.border, color: UI.muted, background: '#fff' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* CTA secundário */}
                <div className="mt-10 text-sm" style={{ color: UI.muted }}>
                  Quer ver uma demo guiada?{' '}
                  <Link to="/contato" className="font-medium hover:opacity-80" style={{ color: UI.text }}>
                    Falar com a equipe
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Planos — catálogo público (read-only, grade + à la carte) */}
      <PricingSection />

      {/* Footer */}
      <footer className="border-t" style={{ borderColor: UI.border }}>
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
          <p style={{ color: UI.muted }}>© {new Date().getFullYear()} VUKZ AI. Todos os direitos reservados.</p>
          <div className="flex gap-6" style={{ color: UI.muted }}>
            <Link to="/privacidade" className="hover:opacity-80 transition-opacity">
              Privacidade
            </Link>
            <Link to="/termos" className="hover:opacity-80 transition-opacity">
              Termos
            </Link>
            <Link to="/contato" className="hover:opacity-80 transition-opacity">
              Contato
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
